// Frozen copy of main's code for the document_generation_v4_enabled / agency_brand_design_enabled flag-off path; delete with the flag.
import React from 'react';

import { mergeBodyHTMLForSave } from './editor-document';

export type EditorSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Debounced save, full-HTML prefix/suffix merge, and refs that track editor vs.
 * persisted content. Shared by workspace artifacts, the documents tab, and
 * library template body editing.
 */
export function useEditorDocumentPersistence(params: {
  content: string;
  onUpdate: (content: string) => void | Promise<void>;
  onSaveStatusChange?: (status: EditorSaveStatus) => void;
}) {
  const { content, onUpdate, onSaveStatusChange } = params;

  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastContentRef = React.useRef<string>(content);
  const lastSavedContentRef = React.useRef<string>(content);
  const isInitialMountRef = React.useRef<boolean>(true);
  const savedTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  /** Ignore stale workspace subscription payloads until the saved doc echoes back. */
  const awaitingSaveEchoRef = React.useRef<boolean>(false);
  /** Supersede in-flight saves when a newer edit is flushed or debounced. */
  const saveGenerationRef = React.useRef(0);

  const documentPrefixRef = React.useRef<string | null>(null);
  const documentSuffixRef = React.useRef<string | null>(null);

  const getContentToSave = React.useCallback(
    (bodyHTML: string) =>
      mergeBodyHTMLForSave(bodyHTML, documentPrefixRef.current, documentSuffixRef.current),
    []
  );

  const executeSave = React.useCallback(
    async (bodyHTML: string, generation: number) => {
      const contentToSave = getContentToSave(bodyHTML);
      onSaveStatusChange?.('saving');
      try {
        await onUpdate(contentToSave);
        if (generation !== saveGenerationRef.current) {
          return;
        }
        lastSavedContentRef.current = contentToSave;
        awaitingSaveEchoRef.current = true;
        onSaveStatusChange?.('saved');
        if (savedTimeoutRef.current) {
          clearTimeout(savedTimeoutRef.current);
        }
        savedTimeoutRef.current = setTimeout(() => {
          onSaveStatusChange?.('idle');
        }, 2000);
      } catch {
        if (generation === saveGenerationRef.current) {
          onSaveStatusChange?.('error');
        }
      }
    },
    [getContentToSave, onSaveStatusChange, onUpdate]
  );

  const debouncedOnUpdate = React.useCallback(
    (bodyHTML: string) => {
      awaitingSaveEchoRef.current = false;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
        savedTimeoutRef.current = null;
      }

      saveGenerationRef.current += 1;
      const generation = saveGenerationRef.current;

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        void executeSave(bodyHTML, generation);
      }, 300);
    },
    [executeSave]
  );

  const flushSave = React.useCallback(
    async (bodyHTML: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
        savedTimeoutRef.current = null;
      }

      saveGenerationRef.current += 1;
      const generation = saveGenerationRef.current;
      await executeSave(bodyHTML, generation);
    },
    [executeSave]
  );

  const cancelPendingSave = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (savedTimeoutRef.current) {
      clearTimeout(savedTimeoutRef.current);
      savedTimeoutRef.current = null;
    }
    saveGenerationRef.current += 1;
    awaitingSaveEchoRef.current = false;
    onSaveStatusChange?.('idle');
  }, [onSaveStatusChange]);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
      }
    };
  }, []);

  return {
    documentPrefixRef,
    documentSuffixRef,
    lastContentRef,
    lastSavedContentRef,
    isInitialMountRef,
    awaitingSaveEchoRef,
    getContentToSave,
    debouncedOnUpdate,
    flushSave,
    cancelPendingSave
  };
}
